import { useState } from "react";
import { MessageCircleQuestion, Pin, Send, X } from "lucide-react";
import { toast } from "../components/ui/sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AMACard = ({ clusters, userProfile, onManage }) => {
  const [activeTab, setActiveTab] = useState("trending");
  const [showForm, setShowForm] = useState(false);
  const [questionText, setQuestionText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmitQuestion = async () => {
    if (!questionText.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/ama/question`, {
        question_text: questionText.trim(),
        user_profile: userProfile,
      });
      toast.success("Question submitted!", { description: "Your question has been added to the queue." });
      setQuestionText("");
      setShowForm(false);
    } catch {
      toast.error("Failed to submit question. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!clusters || clusters.length === 0) {
    return (
      <section className="card-surface p-6 h-full" data-testid="ama-card-empty">
        <h3 className="text-title-lg font-headline text-[#F8FAFC] mb-4">Leaders answer your questions</h3>
        <p className="text-[#94A3B8] text-sm">No questions yet. Be the first to ask!</p>
      </section>
    );
  }

  const sortedClusters = [...clusters].sort((a, b) => (b.priority_score || 0) - (a.priority_score || 0));
  const pinnedResponses = clusters.flatMap(c => c.responses?.filter(r => r.pinned) || []);

  return (
    <section className="card-surface p-6 h-full" data-testid="ama-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <MessageCircleQuestion className="w-5 h-5 text-[#14B8A6]" />
          <h3 className="text-title-lg font-headline text-[#F8FAFC]">Leaders answer your questions</h3>
        </div>
        {userProfile === "leader" && (
          <button
            onClick={onManage}
            className="text-xs text-[#14B8A6] font-medium hover:underline"
            data-testid="ama-manage"
          >
            Manage
          </button>
        )}
      </div>

      <div className="flex gap-2 mb-4">
        {["trending", "clusters"].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              activeTab === tab
                ? "bg-[#14B8A6]/10 text-[#14B8A6]"
                : "text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-white/[0.04]"
            }`}
            data-testid={`ama-tab-${tab}`}
          >
            {tab === "trending" ? "Trending" : "By Theme"}
          </button>
        ))}
        {pinnedResponses.length > 0 && (
          <button
            onClick={() => setActiveTab("pinned")}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors flex items-center gap-1 ${
              activeTab === "pinned"
                ? "bg-[#14B8A6]/10 text-[#14B8A6]"
                : "text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-white/[0.04]"
            }`}
            data-testid="ama-tab-pinned"
          >
            <Pin className="w-3 h-3" /> Pinned
          </button>
        )}
      </div>

      <div className="space-y-2">
        {activeTab === "trending" && sortedClusters.slice(0, 4).map((cluster, idx) => (
          <div
            key={cluster.cluster_id || idx}
            className="p-3 rounded-lg bg-white/[0.02] hover:bg-white/[0.05] border border-white/[0.04] transition-all cursor-pointer group"
            data-testid={`ama-cluster-${idx}`}
          >
            <div className="flex items-start justify-between mb-2">
              <span className="source-badge text-xs px-2 py-0.5 rounded-full capitalize">{cluster.theme}</span>
              <span className="text-xs text-[#94A3B8]">{cluster.question_ids?.length || 0} questions</span>
            </div>
            <h4 className="text-sm font-medium text-[#F8FAFC] group-hover:text-[#14B8A6] transition-colors">{cluster.summary}</h4>
            {cluster.questions?.slice(0, 1).map((q, qIdx) => (
              <p key={qIdx} className="mt-2 text-xs text-[#94A3B8] italic line-clamp-2">"{q.submitted_text}"</p>
            ))}
            {userProfile === "leader" && cluster.leader_tools && (
              <div className="flex gap-2 mt-3">
                <button className="text-xs text-[#14B8A6] hover:underline" data-testid={`ama-respond-${idx}`}>Respond</button>
                <button className="text-xs text-[#94A3B8] hover:text-[#14B8A6]" data-testid={`ama-summarize-${idx}`}>Summarize</button>
              </div>
            )}
          </div>
        ))}

        {activeTab === "clusters" && sortedClusters.map((cluster, idx) => (
          <div
            key={cluster.cluster_id || idx}
            className="p-3 rounded-lg bg-white/[0.02] border border-white/[0.04]"
            data-testid={`ama-theme-${idx}`}
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: `hsl(${(idx * 60) % 360}, 70%, 50%)` }} />
              <span className="text-sm font-medium text-[#F8FAFC] capitalize">{cluster.theme}</span>
              <span className="text-xs text-[#94A3B8] ml-auto">{Math.round((cluster.priority_score || 0) * 100)}% priority</span>
            </div>
            <p className="text-xs text-[#94A3B8]">{cluster.summary}</p>
          </div>
        ))}

        {activeTab === "pinned" && pinnedResponses.map((response, idx) => (
          <div
            key={response.response_id || idx}
            className="p-3 rounded-lg bg-[#6D28D9]/[0.06] border-l-2 border-[#6D28D9]"
            data-testid={`ama-pinned-${idx}`}
          >
            <div className="flex items-center gap-2 mb-2">
              <Pin className="w-3.5 h-3.5 text-[#A78BFA]" />
              <span className="text-xs text-[#A78BFA] font-medium">Pinned Response</span>
            </div>
            <p className="text-xs text-[#F8FAFC]/80">{response.response_text}</p>
          </div>
        ))}
      </div>

      {showForm ? (
        <div className="mt-4 space-y-2" data-testid="ama-question-form">
          <div className="flex items-center justify-between">
            <span className="text-xs text-[#94A3B8] font-medium">Ask a question</span>
            <button onClick={() => { setShowForm(false); setQuestionText(""); }} className="p-1 rounded hover:bg-white/[0.06]" data-testid="ama-form-close">
              <X className="w-3.5 h-3.5 text-[#94A3B8]" />
            </button>
          </div>
          <textarea
            value={questionText}
            onChange={(e) => setQuestionText(e.target.value)}
            placeholder="What would you like leaders to answer?"
            className="w-full h-20 p-3 rounded-lg bg-white/[0.04] border border-white/[0.06] text-[#F8FAFC] placeholder-[#94A3B8]/40 resize-none focus:ring-1 focus:ring-[#14B8A6]/50 focus:outline-none text-sm"
            data-testid="ama-question-input"
          />
          <button
            onClick={handleSubmitQuestion}
            disabled={!questionText.trim() || submitting}
            className="w-full py-2 rounded-md bg-[#14B8A6] text-[#050B14] text-sm font-medium hover:bg-[#0D9488] disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            data-testid="ama-question-submit"
          >
            <Send className="w-3.5 h-3.5" />
            {submitting ? "Submitting..." : "Submit Question"}
          </button>
        </div>
      ) : (
        <button
          onClick={() => setShowForm(true)}
          className="w-full mt-4 py-2.5 rounded-md border border-dashed border-white/10 text-sm text-[#94A3B8] hover:border-[#14B8A6]/30 hover:text-[#14B8A6] hover:bg-[#14B8A6]/[0.03] transition-all"
          data-testid="ama-ask-question"
        >
          + Submit a Question
        </button>
      )}
    </section>
  );
};
